import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axiosClient from '../../api/axiosClient';
import { Lock, Smartphone, QrCode, CheckCircle, XCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';

const UserSettingsPage = () => {
  const { user } = useAuth();

  const [passData, setPassData] = useState({ password: '', confirm: '' });
  
  // 2FA State
  const [qrData, setQrData] = useState(null);
  const [otpCode, setOtpCode] = useState('');

  const passwordMutation = useMutation({
      mutationFn: (data) => axiosClient.post('/user/change-password', data),
      onSuccess: () => { alert("Password Changed!"); setPassData({password:'', confirm:''}); }
  });

  const setup2FAMutation = useMutation({
      mutationFn: () => axiosClient.post('/user/security/2fa/setup'),
      onSuccess: (data) => setQrData(data)
  });

  const enable2FAMutation = useMutation({
      mutationFn: () => axiosClient.post('/user/security/2fa/enable', { secret: qrData?.secret, code: otpCode }),
      onSuccess: () => {
          alert("2FA Enabled!");
          setQrData(null);
          setOtpCode('');
          // Refresh User Profile Context (Assuming context refetch logic exists or page reload)
          window.location.reload(); 
      },
      onError: () => alert("Invalid Code")
  });

  const disable2FAMutation = useMutation({
      mutationFn: () => axiosClient.post('/user/security/2fa/disable'),
      onSuccess: () => {
          alert("2FA Disabled.");
          window.location.reload();
      }
  });

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-fade-in pb-10">
       <h1 className="text-2xl font-bold text-slate-800">Security Settings</h1>

       <div className="grid grid-cols-1 gap-6">
           {/* Password */}
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <h3 className="font-bold text-lg mb-4 flex items-center text-slate-700"><Lock className="w-5 h-5 mr-2 text-red-600"/> Change Password</h3>
                <div className="space-y-3 max-w-md">
                    <input type="password" placeholder="New Password" className="w-full p-2 border rounded" value={passData.password} onChange={e => setPassData({...passData, password: e.target.value})} />
                    <input type="password" placeholder="Confirm Password" className="w-full p-2 border rounded" value={passData.confirm} onChange={e => setPassData({...passData, confirm: e.target.value})} />
                    <button onClick={() => { if(passData.password === passData.confirm) passwordMutation.mutate(passData); else alert("Mismatch"); }} className="bg-slate-800 text-white px-4 py-2 rounded-lg font-bold hover:bg-slate-700">Update Password</button>
                </div>
           </div>

           {/* 2FA */}
           <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg flex items-center text-slate-700"><Smartphone className="w-5 h-5 mr-2 text-purple-600"/> Two-Factor Authentication</h3>
                    {user?.is_2fa_enabled ? (
                        <span className="text-green-600 text-xs font-bold flex items-center"><CheckCircle className="w-4 h-4 mr-1"/> ENABLED</span>
                    ) : (
                        <span className="text-slate-400 text-xs font-bold flex items-center"><XCircle className="w-4 h-4 mr-1"/> DISABLED</span>
                    )}
                </div>
                
                {user?.is_2fa_enabled ? (
                    <div className="text-center bg-slate-50 p-6 rounded-lg">
                        <p className="text-sm text-slate-500 mb-4">Your account is secured. OTP required for login.</p>
                        <button onClick={() => { if(window.confirm("Disable 2FA?")) disable2FAMutation.mutate() }} className="text-red-500 border border-red-200 px-4 py-2 rounded hover:bg-red-50 text-sm font-bold">Disable 2FA</button>
                    </div>
                ) : (
                    !qrData ? (
                        <button onClick={() => setup2FAMutation.mutate()} className="bg-purple-600 text-white px-4 py-2 rounded font-bold hover:bg-purple-700 w-full flex items-center justify-center">
                            <QrCode className="w-4 h-4 mr-2"/> Setup 2FA Now
                        </button>
                    ) : (
                        <div className="space-y-4 animate-in fade-in">
                            <div className="bg-white border p-4 rounded flex justify-center"><img src={qrData.qr_code} alt="QR" className="h-40"/></div>
                            <p className="text-center text-xs text-slate-500 font-mono select-all bg-slate-100 p-1 rounded">{qrData.secret}</p>
                            <input className="w-full p-2 border rounded text-center font-mono tracking-widest text-lg" placeholder="000000" maxLength={6} value={otpCode} onChange={e => setOtpCode(e.target.value)} />
                            <div className="flex gap-2">
                                <button onClick={() => setQrData(null)} className="flex-1 py-2 text-slate-500 border rounded hover:bg-slate-50">Cancel</button>
                                <button onClick={() => enable2FAMutation.mutate()} className="flex-1 bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700">Verify</button>
                            </div>
                        </div>
                    )
                )}
           </div>
       </div>
    </div>
  );
};

export default UserSettingsPage;